import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Stories from 'react-insta-stories';
import { Story } from 'react-insta-stories/dist/interfaces';
import theme from 'config/theme';
import Icon, { IconNames } from 'components/Icon';
import { useAppSelector } from 'helpers/hooks';
import { ImpactSlideName, trackEvent } from 'helpers/mixpanelEvents';
import { useImpactReportShare } from 'hooks/useImpactReportShare';
import { getImpactReportData } from 'selectors/impactReport';
import { getUserData } from 'selectors/user';
import { ImpactReportData } from 'store/impactReport/types';
import { Intro } from './intro';
import { Slide1 } from './slide1';
import { Slide2 } from './slide2';
import { Slide3 } from './slide3';
import { Slide4 } from './slide4';
import { NoImpactReport } from './noImpactReport';
import { ShareCard } from './ShareCard';
import { BREAKPOINTS } from 'constants/breakpoints';
import { preloadVideo } from './preloadVideo';
import { getSlide2Duration, getVideoUrl, hasVideo } from './video';

interface Props {
  isCommunityReportReady: boolean;
  isSlideVisible: boolean;
  showSlides: (state: boolean) => void;
}
export interface ISlideProps {
  isPaused: boolean;
  impactReportData: ImpactReportData;
  getVideoSrc?: () => string;
}

// About 30s after the summary layer appears, which is ~8s into the slide.
const SUMMARY_SLIDE_DURATION = 38000;

type ImpactStory = Story & { name: ImpactSlideName };

// Module-level so its identity is stable: react-insta-stories rebuilds its story state on
// each new `stories` identity, resetting slide progress.
const noDataStories: ImpactStory[] = [
  {
    name: 'no_report',
    duration: 6000,
    content: ({ isPaused }) => (
      <NoImpactReport isPaused={isPaused} isCommunityReportReady={false} />
    ),
  },
];

export const SlideStories: FC<Props> = ({ isSlideVisible, showSlides, isCommunityReportReady }) => {
  // Non-null here: the stories below are only rendered when impactReportData exists
  // (see the noDataStories branch), while getImpactReportData is nullable.
  const impactReportData = useAppSelector(getImpactReportData) as ImpactReportData;
  const userData = useAppSelector(getUserData);
  const [isSummaryShown, setIsSummaryShown] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  // A second navigator.share while the sheet is open rejects, which would resume the slideshow under it.
  const isShareInFlight = useRef(false);
  const hasTrackedCompletion = useRef(false);
  const { generate, cancel, share, status: shareStatus } = useImpactReportShare();

  useEffect(() => {
    showSlides(isSlideVisible);
    if (!isSlideVisible) {
      setIsSummaryShown(false);
      setIsSharing(false);
      isShareInFlight.current = false;
    }
  }, [isSlideVisible, showSlides]);

  // Capture as soon as the summary appears so the image is ready before the user taps Share.
  // Cancelling on back or close keeps an interrupted capture from logging a false failure.
  useEffect(() => {
    if (!isSummaryShown || !shareCardRef.current) return undefined;
    generate(shareCardRef.current);
    return cancel;
  }, [isSummaryShown, generate, cancel]);

  // Blob URL of the preloaded video, once it's downloaded.
  const preloadedVideoSrcRef = useRef<string | null>(null);
  const preloadControllerRef = useRef<AbortController | null>(null);
  // Every blob URL created, revoked on unmount rather than when the data
  // changes, so a video already playing from one doesn't break.
  const blobUrlsRef = useRef<string[]>([]);
  // Read when Slide 2's video layer appears, so a preload that finishes any
  // time before then is used without having to update `stories`.
  const getVideoSrc = useCallback(() => {
    if (preloadedVideoSrcRef.current) return preloadedVideoSrcRef.current;
    // Not downloaded yet: Slide 2 streams from the URL instead, so stop the
    // download rather than fetching the video twice.
    preloadControllerRef.current?.abort();
    return getVideoUrl(impactReportData.video ?? '');
  }, [impactReportData]);

  const [storiesWidth, setStoriesWidth] = useState(390);
  const stories = useMemo<ImpactStory[]>(() => {
    const hasRaces = !!impactReportData?.race_ids?.length;
    const issueCount = impactReportData?.issues?.length ?? 0;

    return [
      {
        name: 'intro',
        duration: 3000,
        content: ({ isPaused }) => <Intro isPaused={isPaused} />,
      },
      {
        name: 'donations',
        duration: 24000,
        content: ({ isPaused }) => (
          <Slide1 isPaused={isPaused} impactReportData={impactReportData} />
        ),
      },
      ...(hasRaces
        ? [
            {
              name: 'races' as const,
              duration: getSlide2Duration(impactReportData),
              content: ({ isPaused }) => (
                <Slide2
                  isPaused={isPaused}
                  impactReportData={impactReportData}
                  getVideoSrc={hasVideo(impactReportData) ? getVideoSrc : undefined}
                />
              ),
            },
          ]
        : []),
      ...(issueCount > 0
        ? [
            {
              name: 'issues' as const,
              duration: issueCount === 1 ? 8000 : 16000,
              content: ({ isPaused }) => (
                <Slide3 isPaused={isPaused} impactReportData={impactReportData} />
              ),
            },
          ]
        : []),
      {
        name: 'summary',
        duration: SUMMARY_SLIDE_DURATION,
        content: ({ isPaused }) => (
          <Slide4
            isPaused={isPaused}
            impactReportData={impactReportData}
            onSummaryShown={() => {
              setIsSummaryShown(true);
              if (hasTrackedCompletion.current) return;
              hasTrackedCompletion.current = true;
              trackEvent('Profile Impact Report Completed', null);
            }}
          />
        ),
      },
    ];
  }, [impactReportData, getVideoSrc]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= BREAKPOINTS.MOBILE) {
        setStoriesWidth(window.innerWidth);
      } else {
        setStoriesWidth(390);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Download Slide 2's video in the background so it plays without
  // buffering when its layer appears. If this fails or hasn't finished (e.g.
  // no CORS on this origin), Slide 2 streams from the URL instead.
  useEffect(() => {
    preloadedVideoSrcRef.current = null;
    if (!hasVideo(impactReportData) || !impactReportData.video) return;
    const controller = new AbortController();
    preloadControllerRef.current = controller;

    preloadVideo(getVideoUrl(impactReportData.video), controller.signal)
      .then(objectUrl => {
        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        blobUrlsRef.current.push(objectUrl);
        preloadedVideoSrcRef.current = objectUrl;
      })
      .catch(() => {});

    return () => controller.abort();
  }, [impactReportData]);

  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => blobUrls.forEach(url => URL.revokeObjectURL(url));
  }, []);

  // Pauses the slideshow so it can't end under the share sheet.
  const handleShare = () => {
    if (isShareInFlight.current) return;
    isShareInFlight.current = true;
    setIsSharing(true);
    share().then(outcome => {
      isShareInFlight.current = false;
      // Stay paused after a share: iOS can settle it while the user is still in the other app, and
      // resuming while hidden ends the slide on return. The next tap resumes.
      if (outcome !== 'shared') setIsSharing(false);
    });
  };

  // Needs to run in the same event as the library's touchend/mouseup handler: if we re-render
  // first, it sees "playing" and skips ahead, closing the report. Share taps are skipped so a
  // share from the held summary stays paused. Also clears the in-flight flag in case a share
  // promise never settles.
  const releaseShareHold = (event: SyntheticEvent) => {
    if (shareButtonRef.current?.contains(event.target as Node)) return;
    isShareInFlight.current = false;
    setIsSharing(false);
  };

  if (!isSlideVisible) return null;

  return (
    <div
      className="impact-stories"
      onTouchEndCapture={releaseShareHold}
      onMouseUpCapture={releaseShareHold}
    >
      <Stories
        stories={!impactReportData ? noDataStories : stories}
        height="100%"
        width={storiesWidth}
        isPaused={isSharing}
        onAllStoriesEnd={() => showSlides(false)}
        onStoryStart={(_index: number, story: ImpactStory) =>
          trackEvent('Profile Impact Report Slide Viewed', { slide: story.name })
        }
        onPrevious={() => setIsSummaryShown(false)}
      />
      {isSummaryShown && (
        <>
          {/* Rendered above the library's tap overlay, which covers the slide content. */}
          <button
            ref={shareButtonRef}
            type="button"
            className="button button-brand button-brand-secondary impact-share-button"
            onClick={handleShare}
            disabled={shareStatus === 'idle' || shareStatus === 'generating'}
          >
            <Icon name={IconNames.Share} color={theme.colors.inkBlue} size={14} />
            Share
          </button>
          <div className="impact-share-capture" aria-hidden="true">
            <ShareCard
              ref={shareCardRef}
              firstName={userData.first_name}
              impactReportData={impactReportData}
            />
          </div>
        </>
      )}
    </div>
  );
};
